import { useCallback, useEffect, useRef, useState } from "react";
import { evaluate, formatResult } from "../lib/maths";
import MathsPreview from "./MathsPreview";
import { X, Save, CornerDownLeft, Delete, Loader2, Check } from "lucide-react";

/**
 * A calculator, with a tape that can be kept as working.
 *
 * The tape is the point. A calculator alone is a phone away; what a tutor
 * cannot otherwise see is *what the child actually keyed*, which is where
 * most lost marks live - a bracket in the wrong place, degrees against
 * radians, a figure rounded three steps too early.
 *
 * **It is saved as the lines they pressed, not as a picture of the screen.**
 * A screenshot of a calculator is text rendered as an image: it cannot be
 * read back, searched, quoted in a comment, or compared with the mark
 * scheme, and it is twenty times the size. The tutor sees the same sum.
 *
 * Nothing here is marked. The tape sits beside the photographed working on
 * the marking screen, as one more thing to read.
 */

const PAD = [
  [
    { t: "sin(", l: "sin" }, { t: "cos(", l: "cos" }, { t: "tan(", l: "tan" },
    { t: "(", l: "(" }, { t: ")", l: ")" },
  ],
  [
    { t: "sqrt(", l: "√" }, { t: "^2", l: "x²" }, { t: "^", l: "xⁿ" },
    { t: "log(", l: "log" }, { t: "ln(", l: "ln" },
  ],
  [
    { t: "7" }, { t: "8" }, { t: "9" }, { t: "/", l: "÷" }, { t: "%", l: "%" },
  ],
  [
    { t: "4" }, { t: "5" }, { t: "6" }, { t: "*", l: "×" }, { t: "π", l: "π" },
  ],
  [
    { t: "3" }, { t: "2" }, { t: "1" }, { t: "-", l: "−" }, { t: "!", l: "n!" },
  ],
  [
    { t: "0" }, { t: "." }, { t: "Ans", l: "Ans" }, { t: "+", l: "+" }, { t: "=", l: "=" },
  ],
];

export default function Calculator({ code, initialTape = [], onSave, onClose, readOnly }) {
  const [expr, setExpr] = useState("");
  const [tape, setTape] = useState(initialTape);
  const [error, setError] = useState(null);
  const [degrees, setDegrees] = useState(true);
  const [ans, setAns] = useState(null);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const inputRef = useRef(null);

  useEffect(() => { inputRef.current?.focus(); }, []);

  const press = (t) => {
    setError(null);
    setSaved(false);
    if (t === "=") return run();
    if (t === "Ans") { setExpr((e) => e + (ans === null ? "" : String(ans))); return; }
    setExpr((e) => e + t);
  };

  const run = useCallback(() => {
    const source = expr.trim();
    if (!source) return;
    try {
      const substituted = ans === null ? source : source.replace(/\bAns\b/g, `(${ans})`);
      const value = evaluate(substituted, { degrees });
      const shown = formatResult(value);
      setTape((t) => [...t, `${source} = ${shown}`].slice(-40));
      setAns(value);
      setExpr("");
      setError(null);
      setSaved(false);
    } catch (e) {
      setError(e.message);
    }
  }, [expr, ans, degrees]);

  const save = () => {
    if (!onSave) return;
    setSaving(true);
    Promise.resolve(onSave(tape))
      .then(() => setSaved(true))
      .catch(() => setError("Could not save that working"))
      .finally(() => setSaving(false));
  };

  return (
    <div className="fixed inset-0 z-[60] bg-slate-900/40 flex items-end sm:items-center justify-center p-0 sm:p-4"
         onClick={onClose}>
      <div className="bg-white rounded-t-2xl sm:rounded-2xl w-full sm:max-w-[380px] shadow-xl"
           onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between px-4 py-3 border-b border-slate-200">
          <div>
            <h2 className="font-semibold text-slate-800 text-sm">Calculator</h2>
            {code && <p className="text-[11px] text-slate-500 font-mono">{code}</p>}
          </div>
          <div className="flex items-center gap-2">
            <button type="button" onClick={() => setDegrees((d) => !d)}
              title="Degrees or radians for sin, cos and tan"
              className="text-[11px] font-semibold border border-slate-300 rounded-lg px-2 py-1 hover:bg-slate-50">
              {degrees ? "DEG" : "RAD"}
            </button>
            <button onClick={onClose} className="text-slate-400 hover:text-slate-700"><X size={18} /></button>
          </div>
        </div>

        {tape.length > 0 && (
          <div className="max-h-[150px] overflow-y-auto px-4 py-2 bg-slate-50 border-b border-slate-200 space-y-1">
            {tape.map((line, i) => (
              <div key={i} className="font-mono text-xs text-slate-600 flex gap-2">
                <span className="text-slate-300 w-4 text-right shrink-0">{i + 1}</span>
                <span className="break-all">{line}</span>
              </div>
            ))}
          </div>
        )}

        <div className="px-4 pt-3">
          <input
            ref={inputRef}
            value={expr}
            onChange={(e) => { setExpr(e.target.value); setError(null); }}
            onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); run(); } }}
            placeholder="type a sum, or use the buttons"
            inputMode="text"
            autoComplete="off"
            className="w-full font-mono text-right text-lg border border-slate-300 rounded-lg px-3 py-2"
          />
          <div className="flex items-center justify-between min-h-[22px] mt-1">
            <span className="text-sm text-slate-500">
              {expr && !error && <MathsPreview value={expr} />}
            </span>
            {ans !== null && !error && (
              <span className="text-xs text-slate-400">Ans = {formatResult(ans)}</span>
            )}
          </div>
          {error && <p className="text-xs text-red-700 mb-1">{error}</p>}
        </div>

        <div className="p-3 pt-1">
          {PAD.map((row, r) => (
            <div key={r} className="grid grid-cols-5 gap-1.5 mb-1.5">
              {row.map((k) => (
                <button key={k.t} type="button" onClick={() => press(k.t)}
                  className={`h-11 rounded-lg text-sm font-medium border ${
                    k.t === "=" ? "bg-slate-800 text-white border-slate-800"
                                : "bg-white border-slate-200 hover:bg-slate-100"}`}>
                  {k.l ?? k.t}
                </button>
              ))}
            </div>
          ))}
          <div className="grid grid-cols-5 gap-1.5">
            <button type="button" onClick={() => setExpr((e) => e.slice(0, -1))}
              className="h-10 rounded-lg border border-slate-200 bg-white hover:bg-slate-100 inline-flex items-center justify-center">
              <Delete size={15} />
            </button>
            <button type="button" onClick={() => { setExpr(""); setError(null); }}
              className="h-10 rounded-lg border border-slate-200 bg-white hover:bg-slate-100 text-sm">
              AC
            </button>
            <button type="button" onClick={run}
              className="col-span-3 h-10 rounded-lg border border-slate-200 bg-white hover:bg-slate-100 text-sm inline-flex items-center justify-center gap-1.5">
              <CornerDownLeft size={14} /> work it out
            </button>
          </div>
        </div>

        {!readOnly && onSave && (
          <div className="px-4 pb-4">
            <button type="button" onClick={save} disabled={saving || tape.length === 0}
              className="w-full inline-flex items-center justify-center gap-2 bg-slate-800 text-white text-sm font-semibold rounded-lg py-2.5 disabled:opacity-40">
              {saving ? <Loader2 size={14} className="animate-spin" /> : saved ? <Check size={14} /> : <Save size={14} />}
              {saving ? "Saving…" : saved ? "Saved with your answer" : `Keep these ${tape.length} line${tape.length === 1 ? "" : "s"} as my working`}
            </button>
            <p className="text-[11px] text-slate-400 mt-1.5 text-center">
              Your tutor sees exactly what you keyed, which is where most lost marks are.
            </p>
            {tape.length > 0 && (
              <button type="button" onClick={() => { setTape([]); setSaved(false); }}
                className="w-full text-[11px] text-slate-400 hover:text-slate-700 mt-1">
                clear the tape
              </button>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
